/**
 * Shared vocabulary for the plan/entitlement model.
 *
 * The billing subject is the *user* (the person), not the account: `accounts`
 * rows are the per-identity entries behind the account switcher (Google,
 * Microsoft, passwordless), N per person, so anchoring a plan there would
 * mean buying Pro once per connected identity.
 */

/**
 * The full tier ladder, ordered lowest to highest. The ORDER IS LOAD-BEARING:
 * entitlement resolution ranks competing claims by position in this array, so
 * a new tier must be inserted at its correct rank, not appended.
 */
export const PLAN_TIERS = ["free", "pro", "power", "max"] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/**
 * How a tier is named to the user. Lives here rather than in the web bundle
 * because the server names tiers too — a dunning notice has to say which plan
 * is at risk — and two surfaces calling the same tier different things is the
 * kind of drift shared types exist to prevent.
 */
export const PLAN_TIER_LABELS: Record<PlanTier, string> = {
  free: "Free",
  pro: "Pro",
  power: "Power",
  max: "Max",
};

/**
 * Tiers a subscription claim may assert. `free` is never stored — it is the
 * absence of any claim — so the DB vocabulary is the ladder minus `free`.
 * The `billing_subscriptions` CHECK constraint is generated from this list.
 */
export const PAID_TIERS = ["pro", "power", "max"] as const satisfies readonly PlanTier[];
export type PaidTier = (typeof PAID_TIERS)[number];

/**
 * The paid tiers ranked above `tier`, lowest first — what an upgrade can
 * offer. Empty at the top of the ladder, where "upgrade" copy would be a lie.
 */
export function paidTiersAbove(tier: PlanTier): PaidTier[] {
  const rank = PLAN_TIERS.indexOf(tier);
  return PAID_TIERS.filter((paid) => PLAN_TIERS.indexOf(paid) > rank);
}

/**
 * How often a paid subscription renews. Purely a price: the usage allowance
 * resets on the same UTC week whichever interval the user pays on. The
 * `billing_subscriptions` CHECK constraint is generated from this list.
 */
export const BILLING_INTERVALS = ["month", "year"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/**
 * Where a subscription claim comes from. `manual` is a real source, not a
 * placeholder: it is how an employee or internal user holds a tier without a
 * provider object behind it.
 */
export const SUBSCRIPTION_SOURCES = ["stripe", "apple", "google", "manual"] as const;
export type SubscriptionSource = (typeof SUBSCRIPTION_SOURCES)[number];

/**
 * Claim lifecycle. `active` and `trialing` confer entitlement; the rest are
 * retained history that resolves as if the row were absent.
 *
 * `past_due` is the interesting one: it is not in `ENTITLING_STATUSES`, but it
 * still confers the tier for the length of the grace period after the payment
 * failed. That second arm lives in `api/src/entitlements/grace.ts` rather than
 * here because it depends on *when* the failure was, which a list of statuses
 * cannot express — so anything asking "does this claim entitle?" must call
 * `claimEntitlesAt`, not test membership in the constant below.
 *
 * Stripe also emits statuses this vocabulary does not model (`incomplete`,
 * `incomplete_expired`, `unpaid`, `paused`). The webhook layer must map them
 * onto this vocabulary (or drop the event) — passing them through raises the
 * `billing_subscriptions` CHECK constraint and turns a webhook into a
 * permanent retry loop.
 */
export const SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "canceled",
  "expired",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Statuses that confer entitlement unconditionally.
 *
 * Not the whole answer, and deliberately so: `past_due` also entitles while
 * its grace period runs. Keeping this constant to the timeless cases means it
 * stays true for the readers that want exactly that, and leaves the
 * time-dependent arm somewhere it can be tested against a clock —
 * `claimEntitlesAt` in `api/src/entitlements/grace.ts`, which is what
 * production code should be calling.
 */
export const ENTITLING_STATUSES: readonly SubscriptionStatus[] = ["active", "trialing"];

/**
 * Statuses whose provider subscription still exists and can still resume
 * billing. Anything that asks "may this user buy a plan?" must use this set,
 * or a customer mid-dunning can be sold a second subscription and billed
 * twice.
 *
 * This is also the set entitlement resolution reads, because `past_due` may
 * or may not entitle depending on where the claim sits in its grace period.
 */
export const LIVE_STATUSES: readonly SubscriptionStatus[] = ["active", "trialing", "past_due"];

/**
 * The window an allowance is measured over. `month` is the UTC calendar month
 * the first tier policy used; `week` is the UTC week starting Monday 00:00,
 * which every later policy uses.
 */
export const ALLOWANCE_PERIODS = ["month", "week"] as const;
export type AllowancePeriod = (typeof ALLOWANCE_PERIODS)[number];

/**
 * Admin-set adjustments to one user's allowance, on top of their tier, each
 * for a bounded window. A `grant` adds units to the allowance; `uncapped`
 * lifts the cap altogether.
 */
export const USAGE_OVERRIDE_KINDS = ["grant", "uncapped"] as const;
export type UsageOverrideKind = (typeof USAGE_OVERRIDE_KINDS)[number];

/** What the overrides active right now add to the tier's allowance. */
export type OverrideEffect = {
  /** Units granted on top of the tier's allowance. */
  extraUnits: number;
  /** True when an `uncapped` override lifts the cap, whatever was granted. */
  uncapped: boolean;
};

/**
 * What a user is entitled to right now, resolved across all of their claims.
 * `allowanceUnits: null` means unlimited.
 */
export type Entitlements = {
  tier: PlanTier;
  /** AI-unit allowance for the current period; null = unlimited. */
  allowanceUnits: number | null;
  /** The window `allowanceUnits` is measured over. */
  allowancePeriod: AllowancePeriod;
  /**
   * Usage relative to the lowest paid tier (1x, 5x, 20x) — the only measure
   * of size the product shows. Null for tiers sold without a multiple.
   */
  multiple: number | null;
  /** Which source's claim won the resolution; null when the tier is free. */
  source: SubscriptionSource | null;
  /** Version of the tier policy the allowance came from. */
  tierPolicyVersion: string;
  /** Current allowance period, half-open [start, end), UTC. */
  periodStart: Date;
  periodEnd: Date;
};

/**
 * Entitlements plus consumption — the shape usage enforcement will consume.
 * AI units only for now; a storage dimension would land as additional fields
 * beside these.
 *
 * `allowanceUnits` here is the effective allowance: the tier's, plus whatever
 * active admin overrides add (null when one lifts the cap). `override` says
 * what they added, and is null when none is active.
 */
export type EntitlementSnapshot = Entitlements & {
  override: OverrideEffect | null;
  /** AI units consumed so far this period. Undercounts under recorder backpressure, never overcounts. */
  unitsUsed: number;
  /** Remaining allowance; null when the allowance is unlimited. Never negative. */
  unitsRemaining: number | null;
  /** Share of the allowance used, 0–100; null when unlimited. See `percentOfAllowance`. */
  percentUsed: number | null;
};

/**
 * Share of an allowance used, as the whole percent users see.
 *
 * Floors, and caps at 99 until the allowance is actually spent, so a bar never
 * reads 100% while work is still allowed and reads exactly 100 once it is
 * refused (usage can overshoot the ceiling, since metering is post-hoc).
 */
export function percentOfAllowance(
  unitsUsed: number,
  allowanceUnits: number | null,
): number | null {
  if (allowanceUnits === null) return null;
  if (allowanceUnits <= 0 || unitsUsed >= allowanceUnits) return 100;
  return Math.min(99, Math.floor((Math.max(0, unitsUsed) / allowanceUnits) * 100));
}

/**
 * Wire discriminator for the one refusal enforcement can issue: the account's
 * finite allowance is spent. Clients branch on this to render an upgrade flow
 * instead of the generic "something broke" treatment — which is the entire
 * point of the typed error, so this string is API surface and must not change.
 */
export const UNITS_EXHAUSTED_REASON = "units_exhausted";

/**
 * The one user-facing sentence for the refusal, shared by the 402 body and
 * the thread status the workflow posts so the two surfaces cannot drift.
 * Named by period because the period is a property of the tier policy in
 * effect, not a constant: a refusal under a monthly policy saying "this week"
 * would contradict the reset date printed beside it.
 */
export function unitsExhaustedMessage(period: AllowancePeriod): string {
  return `You've used all of your plan's included AI usage for this ${period}.`;
}

/**
 * Stamped verbatim on the refusal message's `agentContent`. Doubles as the
 * durable marker for "this thread's last agent word was an exhaustion
 * refusal": later passes read it to suppress repeat refusals, and the
 * upgrade path reads it to know which queued backlogs the refusal is
 * holding. Must never carry run-specific detail.
 */
export const UNITS_EXHAUSTED_AGENT_CONTENT = "AI workflow refused: units exhausted";

/**
 * Body of the HTTP 402 the boundary emits for `UNITS_EXHAUSTED_REASON`.
 * `periodEnd` (and its alias `resetsAt`) is the exclusive end of the current
 * allowance period as an ISO string — when the allowance resets.
 *
 * Deliberately self-contained: it carries everything the upgrade flow renders
 * (what ran out, how much of it, when it resets), so a client — web today,
 * native later — needs no follow-up request to explain the refusal.
 */
export type UnitsExhaustedResp = {
  error: string;
  reason: typeof UNITS_EXHAUSTED_REASON;
  tier: PlanTier;
  periodEnd: string;
  resetsAt: string;
  allowancePeriod: AllowancePeriod;
  /** Always 100 here; carried so clients render the same number the meter does. */
  percentUsed: number;
  /**
   * AI units consumed this period. May exceed the allowance: metering is post-hoc, so in-flight turns can overshoot the ceiling.
   * @deprecated Users never see unit counts; kept for web builds that predate `percentUsed`.
   */
  unitsUsed: number;
  /**
   * The tier's finite allowance for the current period — weekly since the 2026-10 policy, despite the name.
   * @deprecated Kept for web builds that predate `percentUsed`.
   */
  monthlyUnits: number;
};
